// ============================================================
// fondos/planificador.js — CUÁNDO se pinta un fondo animado (módulo PURO)
//
// Dos decisiones del motor de fondos (`fondos/motor.js`) escritas sin DOM y con el reloj
// INYECTADO, para poder probarlas en node (P3 del plan del motor de fondos,
// `CLAUDE_RESUMEN_PLANES/2026-09-24_plan-p3-motor-efectos.md` §2.3):
//
//  1. EL PRESUPUESTO (V5): como mucho 3 fondos animando a la vez. Primero el de la vista previa del
//     panel; después, el de más área visible. El resto se queda en un fotograma estático. Medido en
//     la línea base (F0, 24-09): sin presupuesto, 6 secciones visibles eran 6 lienzos animando.
//  2. LA CADENCIA (E7): 30 fotogramas por segundo REALES a 60, 120 o 144 Hz, con corrección de fase,
//     y el tiempo transcurrido (dt) para que la velocidad no dependa de la pantalla. La usa el bucle
//     único del motor para los lienzos (F2b). Antes, cada clase de `vendor/bg/` llevaba su propio
//     throttle de 33 ms, que caía a menudo a 3 fotogramas de 60 Hz (F0: neural a 20 fps, con el 39 %
//     de los intervalos dentro de ±20 %).
//
// MÓDULO PURO: ES2019 sin `?.` ni `??`, cero imports y cero DOM.
// ============================================================

/** Cuántos fondos animan a la vez, como mucho (decisión técnica del plan maestro §3.4). */
export const PRESUPUESTO = 3;

/** Fotogramas por segundo de los lienzos (la cadencia nominal de hoy: throttle de 33 ms). */
export const FPS = 30;

/**
 * Ventaja del que YA anima al comparar áreas: sin ella, dos secciones de área casi igual se
 * turnarían el último hueco en cada vuelta del scroll (arranca, para, arranca…).
 */
export const HISTERESIS = 1.1;

/**
 * Qué fondos animan.
 *
 * @param {Array<{id: *, visible: boolean, area: number, preview?: boolean, animando?: boolean}>} candidatos
 *   `area` = píxeles visibles de la sección; `animando` = ya estaba animando (histéresis).
 * @param {number} [presupuesto] - por defecto PRESUPUESTO
 * @returns {Array} los `id` que animan, por orden de prioridad. Un candidato NO visible no anima
 *   nunca (tampoco la vista previa: fuera de la pantalla no la ve nadie).
 */
export function elegirAnimados(candidatos, presupuesto) {
    const tope = (typeof presupuesto === 'number' && presupuesto >= 0) ? presupuesto : PRESUPUESTO;
    const visibles = [];
    (candidatos || []).forEach(function (c, i) {
        if (c && c.visible) visibles.push({ c: c, i: i, peso: (Number(c.area) || 0) * (c.animando ? HISTERESIS : 1) });
    });
    // Orden estable: la vista previa primero; después más área (con la ventaja del que ya anima);
    // a igualdad, el orden de llegada (el del documento: así lo registra el motor).
    visibles.sort(function (a, b) {
        if (!!a.c.preview !== !!b.c.preview) return a.c.preview ? -1 : 1;
        if (b.peso !== a.peso) return b.peso - a.peso;
        return a.i - b.i;
    });
    return visibles.slice(0, tope).map(function (v) { return v.c.id; });
}

/** Tope del dt (ms): tras una pausa larga, el efecto da UN paso normal en vez de saltar. */
export const TOPE_DT = 100;

/**
 * Holgura (ms) con la que un fotograma del rAF que llega un poco ANTES de su hora cuenta como a
 * su hora. Más pequeña que medio fotograma a 240 Hz (2,08 ms): así a 120 Hz se pinta en el
 * fotograma exacto (el redondeo de coma flotante lo dejaría fuera) y nunca en el de antes.
 */
const HOLGURA = 2;

/**
 * Un reloj de fotogramas con CORRECCIÓN DE FASE: decide en cada vuelta del rAF si toca pintar y
 * con qué dt. La hora ideal avanza de intervalo en intervalo (no desde el último pintado), así
 * que la media es exactamente `fps` aunque cada fotograma caiga un poco antes o después; si se
 * queda atrás más de un intervalo (una pausa, un fotograma largo), se re-engancha sin ráfaga.
 *
 * @param {number} [fps] - por defecto FPS
 * @returns {{intervalo: number, paso: function(number): number, reiniciar: function(): void}}
 *   `paso(ahora)` (ms, el timestamp del rAF o un reloj falso) devuelve el dt en ms si toca pintar
 *   —en el primero, un intervalo: el efecto da un paso nominal— o -1 si no toca.
 */
export function crearCadencia(fps) {
    const intervalo = 1000 / (fps > 0 ? fps : FPS);
    let proxima = null;
    let ultima = null;
    return {
        intervalo: intervalo,
        paso: function (ahora) {
            if (proxima === null) {
                proxima = ahora + intervalo;
                ultima = ahora;
                return intervalo;
            }
            if (ahora + HOLGURA < proxima) return -1;
            const dt = ahora - ultima;
            ultima = ahora;
            proxima += intervalo;
            if (ahora - proxima > intervalo) proxima = ahora + intervalo;
            return Math.min(dt, TOPE_DT);
        },
        reiniciar: function () {
            proxima = null;
            ultima = null;
        },
    };
}
